import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Helmet } from "react-helmet-async";

type Car = {
  price: number;
  currency?: string;
  year: number;
  mileageKm: number;
  location: string;
  fuel?: string;
  transmission?: string;
};

interface Props {
  cars: Car[];
  make: string;
  model: string;
}

const cap = (s: string) =>
  s
    .split(/[-\s]+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");

const median = (arr: number[]) => {
  if (!arr.length) return 0;
  const s = [...arr].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : Math.round((s[m - 1] + s[m]) / 2);
};

const fmtPrice = (n: number, currency = "EUR") =>
  new Intl.NumberFormat("ro-RO", { style: "currency", currency, maximumFractionDigits: 0 }).format(n);

const fmtKm = (n: number) => new Intl.NumberFormat("ro-RO").format(n) + " km";

export const ModelStatsContent = ({ cars, make, model }: Props) => {
  const stats = useMemo(() => {
    const prices = cars.map((c) => c.price).filter((p) => p > 0);
    const years = cars.map((c) => c.year).filter((y) => y > 1990);
    const kms = cars.map((c) => c.mileageKm).filter((k) => k > 0);
    const currency = cars.find((c) => c.currency)?.currency || "EUR";

    const cityCounts = new Map<string, number>();
    cars.forEach((c) => {
      const city = (c.location || "").split(",")[0].trim();
      if (city) cityCounts.set(city, (cityCounts.get(city) || 0) + 1);
    });
    const topCities = [...cityCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);

    const fuelCounts = new Map<string, number>();
    cars.forEach((c) => {
      const f = (c.fuel || "").toLowerCase();
      if (f) fuelCounts.set(f, (fuelCounts.get(f) || 0) + 1);
    });
    const topFuels = [...fuelCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);

    return {
      count: cars.length,
      minPrice: prices.length ? Math.min(...prices) : 0,
      maxPrice: prices.length ? Math.max(...prices) : 0,
      medPrice: median(prices),
      medYear: median(years),
      medKm: median(kms),
      currency,
      topCities,
      topFuels,
    };
  }, [cars]);

  const makeLabel = cap(make);
  const modelLabel = cap(model);
  const title = `${makeLabel} ${modelLabel}`;

  if (stats.count === 0) return null;

  const priceRange = stats.minPrice
    ? `${fmtPrice(stats.minPrice, stats.currency)} – ${fmtPrice(stats.maxPrice, stats.currency)}`
    : "";

  const faqs = [
    {
      q: `Cât costă un ${title} second-hand în România?`,
      a: `Pe RoAutoMarket sunt ${stats.count} anunțuri ${title}, cu prețuri între ${priceRange}. Prețul median este ${fmtPrice(stats.medPrice, stats.currency)}, pentru un an mediu ${stats.medYear} și rulaj median ${fmtKm(stats.medKm)}. Toți dealerii sunt verificați RISCO.`,
    },
    {
      q: `În ce orașe găsesc ${title} de vânzare?`,
      a: stats.topCities.length
        ? `Cele mai multe anunțuri ${title} sunt în ${stats.topCities.map(([c, n]) => `${c} (${n})`).join(", ")}. Poți filtra pe oraș direct pe pagina de rezultate.`
        : `Anunțurile ${title} sunt distribuite în toată țara.`,
    },
    {
      q: `Ce tipuri de motorizare are ${title}?`,
      a: stats.topFuels.length
        ? `Cele mai frecvente motorizări pentru ${title} pe platformă sunt: ${stats.topFuels
            .map(([f, n]) => `${f} (${n} anunțuri)`)
            .join(", ")}.`
        : `${title} este disponibil în mai multe motorizări. Filtrează după combustibil pentru a găsi opțiunea potrivită.`,
    },
    {
      q: `Cum verific un ${title} înainte de cumpărare?`,
      a: `Verifică istoricul VIN, cere raport de service complet, testează-l cu un mecanic independent și cere factură. Pe RoAutoMarket, dealerii verificați RISCO au istoricul companiei validat automat.`,
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <section className="mb-12 space-y-8">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Stats grid */}
      <Card>
        <CardHeader>
          <CardTitle>Piața {title} pe RoAutoMarket — date live</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div>
              <div className="text-2xl font-bold text-primary">{stats.count}</div>
              <div className="text-sm text-muted-foreground">Anunțuri active</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{fmtPrice(stats.medPrice, stats.currency)}</div>
              <div className="text-sm text-muted-foreground">Preț median</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{stats.medYear || "—"}</div>
              <div className="text-sm text-muted-foreground">An mediu</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{stats.medKm ? fmtKm(stats.medKm) : "—"}</div>
              <div className="text-sm text-muted-foreground">Rulaj median</div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Prețurile {title} pe platformă variază între <strong>{priceRange}</strong>, în funcție de an,
            rulaj, motorizare și dotări. Datele de mai sus sunt calculate în timp real din cele{" "}
            <strong>{stats.count} anunțuri active</strong> pe RoAutoMarket și se actualizează la fiecare listare
            nouă. Toți vânzătorii sunt dealeri verificați RISCO sau proprietari cu date validate.
          </p>
        </CardContent>
      </Card>

      {/* FAQ */}
      <Card>
        <CardHeader>
          <CardTitle>Întrebări frecvente despre {title} second-hand</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {faqs.map((f, i) => (
            <div key={i}>
              <h3 className="font-semibold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </section>
  );
};

export default ModelStatsContent;
